import { translateSeriesName } from "@/common/utils/seriesNameTranslator";
import { DashboardComponentChartProps } from "@/features/company/types/dashboards/dashboardComponent.type";
import { measureTextWidth } from "@/features/company/utils/dashboards/measureTextWidth";
import { numberTickFormatter } from "@/features/company/utils/dashboards/numberTickFormatter";
import { Fragment, useMemo } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartTooltip } from "./components/ChartTooltip";
import ChartXAxisTick from "./components/ChartXAxisTick";
import ChartYAxisTick from "./components/ChartYAxisTick";

const MAX_LABEL_CHARS = 25;

const BarChartComponent = ({
  chartData,
  dashboardComponentUpdatedAt,
}: DashboardComponentChartProps) => {
  const isVertical = chartData?.layout === 'vertical';

  const yAxisWidth = useMemo(() => {
    if (!isVertical) return undefined;

    const key = chartData?.primary_settings?.query_identifier;
    if (!chartData?.data?.length || !key) return 100;

    const maxWidth = chartData.data.reduce((max, row) => {
      const raw = String(row[key] ?? '');
      const label = raw.length > MAX_LABEL_CHARS ? `${raw.slice(0, MAX_LABEL_CHARS)}...` : raw;
      return Math.max(max, measureTextWidth(label));
    }, 0);

    return Math.ceil(maxWidth) + 16;
  }, [dashboardComponentUpdatedAt, isVertical]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={chartData?.data}
        layout={chartData?.layout}
        margin={{
          left: 10,
          top: 10,
          right: isVertical ? 25 : 10,
          bottom: 10,
        }}
      >
        <CartesianGrid
          strokeWidth={1}
          opacity={0.1}
          horizontal={!isVertical}
          vertical={isVertical}
        />

        <YAxis
          type={isVertical ? 'category' : 'number'}
          dataKey={isVertical ? chartData?.primary_settings?.query_identifier : undefined}
          width={yAxisWidth}
          interval={isVertical ? 'preserveStart' : undefined}
          style={isVertical ? undefined : { fontSize: 11 }}
          tickFormatter={isVertical ? undefined : numberTickFormatter}
          tick={isVertical ? (
            <ChartYAxisTick
              chartData={chartData}
              chartDataUpdatedAt={dashboardComponentUpdatedAt}
            />
          ) : undefined}
        />

        <XAxis
          type={isVertical ? 'number' : 'category'}
          dataKey={isVertical ? undefined : chartData?.primary_settings?.query_identifier}
          style={isVertical ? { fontSize: 11 } : { fontSize: 12 }}
          tickFormatter={isVertical ? numberTickFormatter : undefined}
          interval={isVertical ? undefined : 'preserveStart'}
          height={!isVertical && chartData?.data?.length > 3 ? 85 : undefined}
          tick={!isVertical ? (
            <ChartXAxisTick
              chartData={chartData}
              chartDataUpdatedAt={dashboardComponentUpdatedAt}
            />
          ) : undefined}
        />

        <Tooltip
          cursor={{ fill: '#ffffff', opacity: 0.08 }}
          content={<ChartTooltip chartData={chartData} />}
        />

        <Legend
          iconType="rect"
          iconSize={12}
          wrapperStyle={{ fontSize: 11 }}
          labelStyle={{ color: '#ffffffaa' }}
          layout="horizontal"
          align="center"
          verticalAlign="bottom"
          formatter={(dataKey) => chartData?.series_settings[dataKey].label || translateSeriesName(dataKey)}
        />

        {chartData?.series_settings && Object.entries(chartData?.series_settings).map(([key, data]) => (
          <Fragment key={key}>
            <Bar
              dataKey={key}
              fill={data.color}
              isAnimationActive
            />
          </Fragment>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export default BarChartComponent;
